import {
    DropdownMenu,
    DropdownMenuContent,
    DropdownMenuItem,
    DropdownMenuTrigger,
} from '@src/components/ui/dropdown-menu';
import { useShadcnNotification } from '@src/components/ui/notification';

import { PdfAiAnalysisAction } from '@shared/components/PdfAiAnalysisAction';
import PdfViewer from '@shared/components/PdfViewer';
import { useEntityType } from '@shared/hooks/useEntityType';
import { useQueryMutation } from '@shared/hooks/useQueryMutation';
import ProSpinner from '@shared/newComponents/ProSpinner';
import {
    FileUploadFlowVM,
    MobileSignSessionStatus,
} from '@shared/types/process';
import getSignature from '@shared/util/getSignature';
import getUrl from '@shared/util/getUrl';
import {
    attachSignPreparationId,
    CancelDocumentSignCommand,
    classifySignAttemptFailure,
    getCancelDocumentSignReason,
    needsDocumentSignPreparation,
    PrepareDocumentSignCommand,
    ServiceSignPreparationResult,
    shouldCancelSignPreparation,
    shouldNotifySignAttemptFailureInSignFlow,
    SignPreparationFieldValues,
} from '@shared/util/signPreparation';

import { DesktopQrSignFlow, DesktopQrSignFlowRef } from './DesktopQrSignFlow';
import { MobileSignFlow } from './MobileSignFlow';

import {
    ShadcnButton,
    ShadcnSheet,
    useMobileDevice,
    useWindowDimensions,
} from '@shadcn-features/core';
import { ChevronDown, Monitor, QrCode } from 'lucide-react';
import React, {
    forwardRef,
    useCallback,
    useImperativeHandle,
    useRef,
    useState,
} from 'react';
import { useTranslation } from 'react-i18next';

type DesktopSignView = 'document' | 'qr';

export type MobileSignIntent = 'ServiceDocumentSign' | 'ApprovalDecisionSign';

export type SignFlowTriggerOptions = {
    mobileSignIntent?: MobileSignIntent;
};

export type SignFlowTriggerData = {
    buttonId?: string;
    serviceId?: string;
    signButtonActionId?: string | null;
    needsSignPreparation?: boolean;
    signPreparationFields?: SignPreparationFieldValues;
};

export type SignFlowRef<T> = {
    trigger: (
        props: T,
        documentIdArg?: string | null,
        options?: SignFlowTriggerOptions,
    ) => void;
    checkingStatusSignBySessionId?: (sessionId: string) => void;
};

export type MobileSignUrls = {
    create: string;
    status: string;
    cancel: string;
};

export interface SignFlowProps<T> extends React.ComponentPropsWithRef<
    typeof ShadcnSheet
> {
    isSign?: boolean;
    mobileSignUrls?: MobileSignUrls;
    mobileSignServiceId?: string;
    openUri?: (uri: string) => void;
    servicesController?: string;
    onBeforeTrigger?: () => Promise<unknown>;
    onBeforeSign?: () => Promise<void>;
    onAfterSign?: (extraData?: T) => Promise<void>;
    onResumeMobileSign?: (
        status: MobileSignSessionStatus,
        sessionId: string,
    ) => Promise<void>;
    onSignPreparationSaved?: (fields: SignPreparationFieldValues) => void;
    removeMobileSignSessionId?: () => void;
}

const DesktopSignFlow = <T extends SignFlowTriggerData>(
    {
        onBeforeTrigger,
        onBeforeSign,
        onAfterSign,
        onResumeMobileSign: _onResumeMobileSign,
        onSignPreparationSaved,
        mobileSignServiceId,
        mobileSignUrls,
        servicesController = 'flow/Services',
        extra,
        ...props
    }: SignFlowProps<T>,
    ref: React.Ref<SignFlowRef<T>>,
) => {
    const { t, i18n } = useTranslation();
    const notification = useShadcnNotification();
    const { isMobile } = useWindowDimensions();
    const [open, setOpen] = useState(false);
    const { data: entityTypeContext, isError: isEntityTypeError } =
        useEntityType();
    const isLegalEntity = entityTypeContext?.current === 'LegalEntity';
    const isQrSignDisabled =
        isEntityTypeError ||
        (entityTypeContext?.current !== 'Physical' &&
            entityTypeContext?.current !== 'IndividualEntrepreneur');
    const [pdfFile, setPdfFile] = useState<string>();
    const [pdfFileName, setPdfFileName] = useState('document.pdf');
    const [loading, setLoading] = useState(false);
    const [view, setView] = useState<DesktopSignView>('document');
    const additionalDataRef = useRef<T>();
    const mobileSignOptionsRef = useRef<SignFlowTriggerOptions>();
    const requestIdRef = useRef(0);
    const qrSignFlowRef = useRef<DesktopQrSignFlowRef<T>>(null);
    const preparationIdRef = useRef<string | null>(null);
    const signedSuccessfullyRef = useRef(false);

    const getDocumentMutation = useQueryMutation<string, FileUploadFlowVM>(
        'flow/Files/GetBase64/{id}',
        'get',
        {
            variablesOption(url, variables, axiosConfig) {
                return {
                    url: url.replace('{id}', variables),
                    axiosConfig,
                };
            },
        },
    );

    const prepareDocumentSign = useQueryMutation<
        PrepareDocumentSignCommand,
        ServiceSignPreparationResult
    >(getUrl(servicesController, 'PrepareDocumentSign'), 'post');

    const cancelDocumentSign = useQueryMutation<
        CancelDocumentSignCommand,
        unknown
    >(getUrl(servicesController, 'CancelDocumentSign'), 'post', {
        hideSuccessNotification: true,
    });

    const cancelActivePreparation = useCallback(
        async (kind: 'sheetClosed' | 'userCancelled' | 'clientError') => {
            const preparationId = preparationIdRef.current;
            if (
                !shouldCancelSignPreparation({
                    preparationId,
                    signedSuccessfully: signedSuccessfullyRef.current,
                }) ||
                !preparationId
            )
                return;

            preparationIdRef.current = null;
            try {
                await cancelDocumentSign.mutateAsync({
                    preparationId,
                    reason: getCancelDocumentSignReason(kind),
                });
            } catch (error) {
                console.error(error, 'cancelDocumentSign');
            }
        },
        [cancelDocumentSign],
    );

    const closeDrawer = useCallback(() => {
        requestIdRef.current += 1;
        qrSignFlowRef.current?.cancel();
        setView('document');
        void cancelActivePreparation('sheetClosed');
        setPdfFile(undefined);
        setPdfFileName('document.pdf');
        setOpen(false);
    }, [cancelActivePreparation]);

    const signDocument = useCallback(async () => {
        try {
            setLoading(true);
            await onBeforeSign?.();
            if (!pdfFile) return;

            let signature = await getSignature(pdfFile, {
                locale: i18n.language,
            });
            signature = signature.replaceAll('-----BEGIN CMS-----', '');
            signature = signature.replaceAll('-----END CMS-----', '');
            signature = signature.replaceAll('\r\n', '');

            const preparationId = preparationIdRef.current;

            await onAfterSign?.(
                attachSignPreparationId(
                    {
                        signData: signature,
                        ...additionalDataRef.current,
                        buttonProps: null,
                    },
                    preparationId,
                ) as unknown as T,
            );
            signedSuccessfullyRef.current = true;
            preparationIdRef.current = null;
            closeDrawer();
        } catch (err) {
            signedSuccessfullyRef.current = false;
            const kind = classifySignAttemptFailure(err);
            if (!shouldNotifySignAttemptFailureInSignFlow(kind)) {
                console.error(err, 'signError');
                return;
            }
            if (kind === 'ncalayerError') {
                notification.error({
                    message: t('translation:ncaLayerError'),
                    description: t('translation:ncaLayerError'),
                });
            } else if (kind === 'ncalayerConnectionClosed') {
                notification.error({
                    message: t('translation:ncaLayerConnectionClosed'),
                    description: t('translation:ncaLayerConnectionClosed'),
                });
            } else if (kind === 'userCancelled') {
                notification.error({
                    message: t('translation:ecpCancelled'),
                    description: t('translation:ecpCancelled'),
                });
            }
        } finally {
            setLoading(false);
        }
    }, [
        closeDrawer,
        i18n.language,
        notification,
        onAfterSign,
        onBeforeSign,
        pdfFile,
        t,
    ]);

    const completeQrSign = useCallback(
        async (extra?: T) => {
            const preparationId = preparationIdRef.current;
            try {
                await onAfterSign?.(
                    attachSignPreparationId(
                        extra ?? ({} as T),
                        preparationId,
                    ) as T,
                );
                signedSuccessfullyRef.current = true;
                preparationIdRef.current = null;
            } catch (error) {
                signedSuccessfullyRef.current = false;
                throw error;
            }
        },
        [onAfterSign],
    );

    const startMobileSign = useCallback(() => {
        if (isQrSignDisabled || !additionalDataRef.current) return;
        setView('qr');
        void qrSignFlowRef.current?.start(
            additionalDataRef.current,
            mobileSignOptionsRef.current,
            preparationIdRef.current,
        );
    }, [isQrSignDisabled]);

    const handleDocumentLoaded = useCallback(
        async (base64: string) => {
            try {
                await onBeforeTrigger?.();
                if (!base64) throw new Error('base64 not found');
                setPdfFile(base64);
                setOpen(true);
            } catch (error) {
                closeDrawer();
                console.error(error, 'signDocumentLoadError');
            }
        },
        [closeDrawer, onBeforeTrigger],
    );

    useImperativeHandle(
        ref,
        () => ({
            async trigger(data, documentIdArg, options) {
                const requestId = requestIdRef.current + 1;
                requestIdRef.current = requestId;
                additionalDataRef.current = data;
                mobileSignOptionsRef.current = options;
                qrSignFlowRef.current?.cancel();
                setView('document');
                await cancelActivePreparation('sheetClosed');
                signedSuccessfullyRef.current = false;
                setPdfFile(undefined);
                setOpen(true);

                const needsPrepare = needsDocumentSignPreparation(data);

                try {
                    if (needsPrepare) {
                        const prepared = await prepareDocumentSign.mutateAsync({
                            serviceId: data.serviceId!,
                            buttonId: data.buttonId!,
                            buttonActionId: data.signButtonActionId!,
                            fields: data.signPreparationFields ?? {},
                        });
                        const superseded = additionalDataRef.current !== data;
                        if (data.signPreparationFields && !superseded) {
                            onSignPreparationSaved?.(
                                data.signPreparationFields,
                            );
                        }
                        if (requestId !== requestIdRef.current) {
                            try {
                                await cancelDocumentSign.mutateAsync({
                                    preparationId: prepared.preparationId,
                                    reason: getCancelDocumentSignReason(
                                        'sheetClosed',
                                    ),
                                });
                            } catch (cancelError) {
                                console.error(
                                    cancelError,
                                    'cancelDocumentSignStale',
                                );
                            }
                            return;
                        }
                        preparationIdRef.current = prepared.preparationId;
                        setPdfFileName(prepared.fileName || 'document.pdf');
                        await handleDocumentLoaded(prepared.fileBase64);
                        return;
                    }

                    if (!documentIdArg) {
                        notification.error({
                            message: t('translation:signError'),
                            description: t('translation:signErrorDescription'),
                        });
                        closeDrawer();
                        return;
                    }

                    const dataFile =
                        await getDocumentMutation.mutateAsync(documentIdArg);
                    if (requestId !== requestIdRef.current) return;
                    if (dataFile.content) {
                        setPdfFileName(dataFile.name || 'document.pdf');
                        await handleDocumentLoaded(dataFile.content);
                    }
                } catch (error) {
                    console.error(error, 'signFlowTrigger');
                    notification.error({
                        message: t('translation:signError'),
                        description: t('translation:signErrorDescription'),
                    });
                    closeDrawer();
                }
            },
        }),
        [
            cancelActivePreparation,
            cancelDocumentSign,
            closeDrawer,
            getDocumentMutation,
            handleDocumentLoaded,
            notification,
            onSignPreparationSaved,
            prepareDocumentSign,
            t,
        ],
    );

    const isPending =
        getDocumentMutation.isPending || prepareDocumentSign.isPending;

    const signButton = mobileSignServiceId ? (
        <DropdownMenu>
            <DropdownMenuTrigger asChild>
                <ShadcnButton
                    disabled={!pdfFile}
                    loading={isPending || loading}
                    size='sm'
                    type='button'
                    variant='brandGreen'
                >
                    {t('translation:sign')}
                    <ChevronDown aria-hidden='true' size={16} />
                </ShadcnButton>
            </DropdownMenuTrigger>
            <DropdownMenuContent
                align='end'
                className='p-2!'
                style={{ zIndex: 51 }}
            >
                <DropdownMenuItem
                    className='flex items-center gap-2'
                    onSelect={() => void signDocument()}
                >
                    <Monitor aria-hidden='true' size={16} />
                    {t('translation:signWithNcaLayer')}
                </DropdownMenuItem>
                <DropdownMenuItem
                    className='flex items-center gap-2 data-disabled:cursor-default! data-disabled:bg-[#f0f0f0]! data-disabled:text-[#302a36]/60! dark:data-disabled:bg-white/4! dark:data-disabled:text-white/60!'
                    disabled={isQrSignDisabled}
                    onSelect={startMobileSign}
                >
                    <QrCode aria-hidden='true' size={16} />
                    <span>
                        {t('translation:signWithQr')}
                        {isLegalEntity && (
                            <span className='block'>
                                ({t('translation:comingSoon')})
                            </span>
                        )}
                    </span>
                </DropdownMenuItem>
            </DropdownMenuContent>
        </DropdownMenu>
    ) : (
        <ShadcnButton
            disabled={!pdfFile}
            loading={isPending || loading}
            size='sm'
            type='button'
            variant='brandGreen'
            onClick={signDocument}
        >
            {t('translation:sign')}
        </ShadcnButton>
    );

    const signActions = (
        <>
            <ShadcnButton
                loading={isPending}
                size='sm'
                variant='default'
                onClick={closeDrawer}
            >
                {t('translation:cancel')}
            </ShadcnButton>
            {signButton}
        </>
    );

    return (
        <ShadcnSheet
            {...props}
            open={open}
            title={t('translation:signDocument')}
            width='100vw'
            extra={
                <div className='mr-2 flex items-center gap-2'>
                    {extra}
                    {view === 'document' && pdfFile && (
                        <PdfAiAnalysisAction
                            loadFile={() => ({
                                content: pdfFile,
                                extension: 'application/pdf',
                                name: pdfFileName,
                            })}
                        />
                    )}
                    {view === 'document' && !isMobile && signActions}
                </div>
            }
            footer={
                view === 'document' && isMobile ? (
                    <div className='flex w-full gap-3 sm:w-auto sm:justify-end max-md:[&>*]:flex-1'>
                        {signActions}
                    </div>
                ) : undefined
            }
            className='!fixed !inset-0 !h-[100dvh] !max-h-none !w-screen !max-w-none !rounded-none md:gap-0'
            styles={{
                body: {
                    display: 'flex',
                    minHeight: 0,
                    overflow: 'hidden',
                    padding: 0,
                },
                footer: { padding: '16px 24px' },
            }}
            onClose={closeDrawer}
            onInteractOutside={(event) => event.preventDefault()}
        >
            <ProSpinner className='h-full w-full' spinning={isPending}>
                {view === 'document' && pdfFile && (
                    <PdfViewer
                        className='h-[80dvh] md:h-[92dvh]'
                        loading={!pdfFile}
                        pdf={pdfFile}
                    />
                )}
                {mobileSignServiceId && (
                    <DesktopQrSignFlow
                        ref={qrSignFlowRef}
                        mobileSignServiceId={mobileSignServiceId}
                        mobileSignUrls={mobileSignUrls}
                        visible={view === 'qr'}
                        onAfterSign={completeQrSign}
                        onBack={() => setView('document')}
                        onBeforeSign={onBeforeSign}
                        onClose={closeDrawer}
                    />
                )}
            </ProSpinner>
        </ShadcnSheet>
    );
};

const ForwardedDesktopSignFlow = forwardRef(DesktopSignFlow) as <
    T extends SignFlowTriggerData,
>(
    props: SignFlowProps<T> & { ref?: React.Ref<SignFlowRef<T>> },
) => ReturnType<typeof DesktopSignFlow>;

const InnerSignFlow = <T extends SignFlowTriggerData>(
    props: SignFlowProps<T>,
    ref: React.Ref<SignFlowRef<T>>,
) => {
    const isMobileDevice = useMobileDevice();
    const { mobileSignServiceId, mobileSignUrls, openUri, ...flowProps } =
        props;

    return isMobileDevice && mobileSignServiceId ? (
        <MobileSignFlow
            {...flowProps}
            ref={ref}
            mobileSignServiceId={mobileSignServiceId}
            mobileSignUrls={mobileSignUrls}
            openUri={openUri}
        />
    ) : (
        <ForwardedDesktopSignFlow
            {...flowProps}
            ref={ref}
            mobileSignServiceId={mobileSignServiceId}
            mobileSignUrls={mobileSignUrls}
        />
    );
};

export const SignFlow = forwardRef(InnerSignFlow) as <
    T extends SignFlowTriggerData,
>(
    props: SignFlowProps<T> & { ref?: React.Ref<SignFlowRef<T>> },
) => ReturnType<typeof InnerSignFlow>;
